import React from 'react';
import { connectWithShell, Shell } from 'repluggable';
import {
  A11yWizardPanel,
  A11yWizardProvider,
  EditorHost,
} from '@wix/a11y-wizard';
import { A11Y_BM_SCAN_PENDING_PARAM } from '@wix/a11y-wizard-common';
import type {
  AccessibilityWizardPanelState,
  InitialData,
  MultiLingualData,
} from '@wix/a11y-wizard';

import { ExperimentsAPI } from '@wix/editorx-core-api';
import {
  A11yAdapterApiKey,
  A11yWizardAdapterAPI,
  EditorAPIKey,
  EditorParamsApiKey,
} from './a11y-editor-wizard-api-key';
import { A11yWizardDuplexerAPI, DuplexerData } from '@wix/a11y-adapters/studio';
import { A11yWizardStateAPI } from '../common/a11y-wizard-state-api';

const ProGalleryAppDefId = '14271d6f-ba62-d045-549b-ab972ae1f70e';
const WixStoresAppDefId = '1380b703-ce81-ff05-f115-39571d94dfcd';
const META_SITE_APPLICATION_ID = '-666';

type WizardStateProps = {
  a11yWizardAdapter: A11yWizardAdapterAPI;
  multiLingualData: MultiLingualData;
  locale: string;
  editorSessionId: string;
  metaSiteId: string;
  signedInstance: string | undefined;
  experiments: any;
  referral: string | undefined;
  shouldAutoScan: boolean;
  shouldQueryLatestScan: boolean;
  installedAppIds: string[];
  galleryInstance: string | undefined;
  storesInstance: string | undefined;
  initialData: InitialData | undefined;
  isDebugMode: boolean;
  duplexerData: DuplexerData | undefined;
  panelState: AccessibilityWizardPanelState | undefined;
  userPermissions: string[];
};

const mapStateToProps = (shell: Shell): WizardStateProps => {
  const a11yWizardAdapterAPI = shell.getAPI(A11yAdapterApiKey);
  const editorAPI = shell.getAPI(EditorAPIKey);
  const experimentsAPI = shell.getAPI(ExperimentsAPI);
  const editorParamsAPI = shell.getAPI(EditorParamsApiKey);
  const a11yWizardStateAPI = shell.getAPI(A11yWizardStateAPI);
  const a11yWizardDuplexerAPI = shell.getAPI(A11yWizardDuplexerAPI);

  const locale = editorAPI.generalInfo.getLanguage();
  const metaSiteId = editorAPI.generalInfo.getMetaSiteId();

  const currentLanguage = a11yWizardAdapterAPI.getCurrentLanguage();
  const isMultilingualEnabled = a11yWizardAdapterAPI.getIsMultilingualEnabled();
  const multiLingualLanguages = a11yWizardAdapterAPI.getMultilingualLanguages();

  const multiLingualData = {
    currentLanguage,
    isMultilingualEnabled,
    multiLingualLanguages,
  } as MultiLingualData;

  const urlParams = new URLSearchParams(window.location.search);
  const installedAppIds = editorAPI.platform
    .getInstalledApps()
    .map(({ appDefinitionId }) => appDefinitionId!);

  const editorSessionId = editorParamsAPI.esi;

  const galleryInstance =
    editorAPI.platform.getAppDataByAppDefId(ProGalleryAppDefId)?.instance;
  const storesInstance =
    editorAPI.platform.getAppDataByAppDefId(WixStoresAppDefId)?.instance;

  const userPermissions = editorAPI.generalInfo.getUserPermissions();

  const isDebugMode = urlParams.get('a11yDebug') === 'true';
  const isAccessibilityDeeplink = urlParams.get('deeplink') === 'accessibility';
  const shouldQueryLatestScan =
    isAccessibilityDeeplink &&
    urlParams.get(A11Y_BM_SCAN_PENDING_PARAM) === 'true';
  const referral = isAccessibilityDeeplink
    ? urlParams.get('referralInfo') || undefined
    : undefined;
  const shouldAutoScan =
    !shouldQueryLatestScan &&
    isAccessibilityDeeplink &&
    urlParams.get('autoA11yScan') === 'true';
  const signedInstance = editorAPI.platform.getAppDataByApplicationId(
    META_SITE_APPLICATION_ID,
  )?.instance;

  const initialData = a11yWizardStateAPI.getInitialData();
  const duplexerData = a11yWizardDuplexerAPI.getDuplexerData();
  const panelState = a11yWizardStateAPI.getPanelState();

  return {
    a11yWizardAdapter: a11yWizardAdapterAPI,
    experiments: experimentsAPI.all(),
    metaSiteId,
    referral,
    shouldAutoScan,
    shouldQueryLatestScan,
    multiLingualData,
    installedAppIds,
    signedInstance,
    locale,
    editorSessionId,
    galleryInstance,
    initialData,
    isDebugMode,
    storesInstance,
    duplexerData,
    panelState,
    userPermissions,
  };
};

function mapDispatchToProps(shell: Shell) {
  const a11yWizardStateAPI = shell.getAPI(A11yWizardStateAPI);
  const a11yWizardDuplexerAPI = shell.getAPI(A11yWizardDuplexerAPI);

  const setInitialData = a11yWizardStateAPI.setInitialData;
  const initDuplexer = a11yWizardDuplexerAPI.connect;
  const setPanelState = a11yWizardStateAPI.setPanelState;

  return {
    setInitialData,
    initDuplexer,
    setPanelState,
  };
}

export const createA11yEditorWizardPanel = (boundShell: Shell) =>
  connectWithShell(
    mapStateToProps,
    mapDispatchToProps,
    boundShell,
  )(
    ({
      a11yWizardAdapter,
      locale,
      metaSiteId,
      signedInstance,
      multiLingualData,
      editorSessionId,
      experiments,
      referral,
      shouldAutoScan,
      shouldQueryLatestScan,
      installedAppIds,
      galleryInstance,
      initialData,
      isDebugMode,
      storesInstance,
      duplexerData,
      initDuplexer,
      setPanelState,
      panelState,
      userPermissions,
    }) => (
      <A11yWizardProvider
        metaSiteId={metaSiteId}
        referral={referral}
        shouldAutoScan={shouldAutoScan}
        shouldQueryLatestScan={shouldQueryLatestScan}
        locale={locale}
        signedInstance={signedInstance}
        multiLingualData={multiLingualData}
        editorSessionId={editorSessionId}
        a11yWizardAdapter={a11yWizardAdapter}
        experiments={experiments}
        installedAppIds={installedAppIds}
        galleryInstance={galleryInstance}
        storesInstance={storesInstance}
        initialData={initialData}
        isDebugMode={isDebugMode}
        editorHost={EditorHost.editor}
        duplexerData={duplexerData}
        initDuplexer={initDuplexer}
        onPanelStateChange={setPanelState}
        initialPanelState={panelState}
        userPermissions={userPermissions}
      >
        <A11yWizardPanel />
      </A11yWizardProvider>
    ),
  );
